import { inject, Injectable } from '@angular/core';
import { DEFAULT_MAX_RESULT_COUNT, ZnSelectQueryParams } from '@zendra/shared';
import { PropertyService } from 'cbd-proxies';
import { DistributionService, FlowTypeService } from 'cbd-proxies/planning/proxy';
import { forkJoin, map, Observable, switchMap } from 'rxjs';
import { StandDivisionAndFusionService } from 'orchestrator/proxy';
import type {
  FinalizeStandDivisionAndFusionInputDto,
  FinalizeStandDivisionAndFusionOutputDto,
  StandDivisionAndFusionAdvanceValidationInputDto,
  StandDivisionAndFusionAreaMaintenanceValidationInputDto,
  StandDivisionAndFusionFusedStandAdvanceValidationInputDto,
  StandDivisionAndFusionFusedStandDetailDto,
  StandDivisionAndFusionFusedStandLookupDto,
  StandDivisionAndFusionFusedStandValidationInputDto,
  StandDivisionAndFusionStandDetailDto,
  StandDivisionAndFusionStandLookupDto,
  StandDivisionAndFusionStandValidationInputDto,
} from 'projects/orchestrator/src/lib/proxy/orchestrator/dtos/stand-division-and-fusion/models';
import { DEFAULT_PROPERTIES_MAX_RESULT_COUNT } from '../../contract-assignment-by-stand/contract-assignment-by-stand.config';
import { resolveCatalogId } from '../../utils/catalog-resolution';

const STAND_DIVISION_AND_FUSION_FLOW_TYPE_NAME = 'División&Fusión';
const NO_APPLICABLE_DISTRIBUTION_NAME = 'No aplica';
const PENDING_DISTRIBUTION_NAME = 'Pendiente';
const FINALIZATION_CATALOG_MAX_RESULT_COUNT = 20;

export type StandSelectOption = {
  key: string;
  value: string;
  lookup: StandDivisionAndFusionStandLookupDto;
};

export type FusedStandSelectOption = {
  key: string;
  value: string;
  lookup: StandDivisionAndFusionFusedStandLookupDto;
};

export type StandDivisionAndFusionFinalizeRequest = Omit<
  FinalizeStandDivisionAndFusionInputDto,
  'flowTypeId' | 'noApplicableDistributionId' | 'pendingDistributionId'
>;

@Injectable({
  providedIn: 'root',
})
export class StandDivisionAndFusion {
  private readonly propertyService = inject(PropertyService);
  private readonly distributionService = inject(DistributionService);
  private readonly flowTypeService = inject(FlowTypeService);
  private readonly standDivisionFusionService = inject(StandDivisionAndFusionService);

  loadProperties(
    params?: ZnSelectQueryParams,
  ): Observable<{ items: { key: string; value: string }[]; totalCount: number }> {
    return this.propertyService
      .getList({
        locationIsActive: true,
        name: params?.search,
        skipCount: params?.skipCount,
        maxResultCount: params?.maxResultCount ?? DEFAULT_PROPERTIES_MAX_RESULT_COUNT,
      })
      .pipe(
        map(response => ({
          items: (response.items ?? []).map(item => ({
            key: String(item.id),
            value: item.name ?? String(item.id),
          })),
          totalCount: response.totalCount ?? 0,
        })),
      );
  }

  searchStandOptions(
    propertyId: number,
    params?: ZnSelectQueryParams,
  ): Observable<{ items: StandSelectOption[]; totalCount: number }> {
    return this.standDivisionFusionService
      .searchStands({
        propertyId,
        filter: params?.search,
        skipCount: params?.skipCount,
        maxResultCount: params?.maxResultCount ?? DEFAULT_MAX_RESULT_COUNT,
      })
      .pipe(
        map(response => ({
          items: (response.items ?? []).map(item => ({
            key: String(item.id),
            value: item.name ?? String(item.id),
            lookup: item,
          })),
          totalCount: response.totalCount ?? 0,
        })),
      );
  }

  searchFusedStandOptions(
    propertyId: number,
    params?: ZnSelectQueryParams,
  ): Observable<{ items: FusedStandSelectOption[]; totalCount: number }> {
    return this.standDivisionFusionService
      .searchFusedStands({
        propertyId,
        filter: params?.search,
        skipCount: params?.skipCount,
        maxResultCount: params?.maxResultCount ?? DEFAULT_MAX_RESULT_COUNT,
      })
      .pipe(
        map(response => ({
          items: (response.items ?? []).map(item => ({
            key: String(item.id),
            value: item.name ?? String(item.id),
            lookup: item,
          })),
          totalCount: response.totalCount ?? 0,
        })),
      );
  }

  validateStand(
    input: StandDivisionAndFusionStandValidationInputDto,
  ): Observable<StandDivisionAndFusionStandDetailDto> {
    return this.standDivisionFusionService.validateStand(input);
  }

  validateAreaMaintenance(
    input: StandDivisionAndFusionAreaMaintenanceValidationInputDto,
  ): Observable<void> {
    return this.standDivisionFusionService.validateAreaMaintenance(input);
  }

  validateFusedStand(
    input: StandDivisionAndFusionFusedStandValidationInputDto,
  ): Observable<StandDivisionAndFusionFusedStandDetailDto> {
    return this.standDivisionFusionService.validateFusedStand(input);
  }

  ensureCanAdvance(
    input: StandDivisionAndFusionAdvanceValidationInputDto,
  ): Observable<void> {
    return this.standDivisionFusionService.ensureCanAdvance(input);
  }

  ensureFusedStandCanAdvance(
    input: StandDivisionAndFusionFusedStandAdvanceValidationInputDto,
  ): Observable<void> {
    return this.standDivisionFusionService.ensureFusedStandCanAdvance(input);
  }

  finalize(
    input: StandDivisionAndFusionFinalizeRequest,
  ): Observable<FinalizeStandDivisionAndFusionOutputDto> {
    return forkJoin({
      flowTypes: this.flowTypeService.getList({
        name: STAND_DIVISION_AND_FUSION_FLOW_TYPE_NAME,
        maxResultCount: FINALIZATION_CATALOG_MAX_RESULT_COUNT,
      }),
      noApplicableDistributions: this.distributionService.getList({
        name: NO_APPLICABLE_DISTRIBUTION_NAME,
        maxResultCount: FINALIZATION_CATALOG_MAX_RESULT_COUNT,
      }),
      pendingDistributions: this.distributionService.getList({
        name: PENDING_DISTRIBUTION_NAME,
        maxResultCount: FINALIZATION_CATALOG_MAX_RESULT_COUNT,
      }),
    }).pipe(
      map(({ flowTypes, noApplicableDistributions, pendingDistributions }) => ({
        flowTypeId: resolveCatalogId(
          flowTypes.items,
          STAND_DIVISION_AND_FUSION_FLOW_TYPE_NAME,
          'FlowType',
        ),
        noApplicableDistributionId: resolveCatalogId(
          noApplicableDistributions.items,
          NO_APPLICABLE_DISTRIBUTION_NAME,
          'Distribution',
        ),
        pendingDistributionId: resolveCatalogId(
          pendingDistributions.items,
          PENDING_DISTRIBUTION_NAME,
          'Distribution',
        ),
      })),
      switchMap(catalogIds =>
        this.standDivisionFusionService.finalize({
          ...input,
          ...catalogIds,
        }),
      ),
    );
  }

}
